import React from "react";

import EditableTitle from "../../../../App/Common/Components/EditableTitle";
import { IClientPatch, IPatchAction } from "../../../../components/course-author/helpers/patches";
import { H1 } from "../../../../components/utils/Headings";
import { IUsersUIData } from "../../../../parsers/resolve";
import { ITabInformation, TabButtons, TabsContainer } from "../../../Tabs";
import { IClass, ICourse } from "../../../types/common";
import {
  addNewGoal,
  addNewManager,
  addNewMember,
  deleteGoal,
  modifyClassTitlePatch,
  removeManager,
  removeMember,
} from "../../helpers/patches";
import Goals from "../Goals";
import Settings from "../Settings";
import { DefaultNames } from "../types";
import UsersAndGroupsManagement, { UserType } from "../UsersAndGroupsManagement";

export enum ClassTabOption {
  Goals = "goals",
  Members = "members",
  Managers = "managers",
  Settings = "settings",
}

const tabs: Array<ITabInformation<ClassTabOption>> = [
  { queryParam: "goals", tabItem: ClassTabOption.Goals, title: "Goals" },
  { queryParam: "members", tabItem: ClassTabOption.Members, title: "Members" },
  {
    queryParam: "managers",
    tabItem: ClassTabOption.Managers,
    title: "Managers",
  },
  {
    queryParam: "settings",
    tabItem: ClassTabOption.Settings,
    title: "Settings",
  },
];

interface IClassTabsProps {
  allCourses: ICourse[];
  allMembers: IUsersUIData[];
  allManagers: IUsersUIData[];
  accountId: string;
  orgId: string;
  classId: string;
  class: IClass;
  deleteClass: () => void;
  initialTab: ClassTabOption;
  patchClass: (patch: IClientPatch<IPatchAction>) => void;
}

const ClassTabs: React.FC<IClassTabsProps> = props => {
  const [tab, setTab] = React.useState(props.initialTab);
  const setSelected = (selectedTab: ClassTabOption) => () => setTab(selectedTab);
  const modifyTitle = (title: string) => props.patchClass(modifyClassTitlePatch(props.class, props.accountId, title));
  const addGoal = (courseId: string) => props.patchClass(addNewGoal(props.class, props.accountId, courseId));
  const deleteOldGoal = (coursePosId: number) => {
    props.patchClass(deleteGoal(props.class, props.accountId, coursePosId));
  };
  const addMember = (userId: string) => props.patchClass(addNewMember(props.class, props.accountId, userId));
  const deleteMember = (userId: string) => props.patchClass(removeMember(props.class, props.accountId, userId));
  const addManager = (userId: string) => props.patchClass(addNewManager(props.class, props.accountId, userId));
  const deleteManager = (userId: string) => props.patchClass(removeManager(props.class, props.accountId, userId));
  const renderTab = () => {
    switch (tab) {
      case ClassTabOption.Managers:
        return (
          <UsersAndGroupsManagement
            allUsers={props.allManagers}
            userType={UserType.Manager}
            class={props.class}
            classId={props.classId}
            currentOrgId={props.orgId}
            currentUserId={props.accountId}
            add={addManager}
            remove={deleteManager}
          />
        );
      case ClassTabOption.Members:
        return (
          <UsersAndGroupsManagement
            allUsers={props.allMembers}
            userType={UserType.Member}
            class={props.class}
            classId={props.classId}
            currentOrgId={props.orgId}
            currentUserId={props.accountId}
            add={addMember}
            remove={deleteMember}
          />
        );
      case ClassTabOption.Settings:
        return <Settings class={props.class} deleteClass={props.deleteClass} />;
      default:
        return <Goals allCourses={props.allCourses} addGoal={addGoal} class={props.class} deleteGoal={deleteOldGoal} />;
    }
  };

  return (
    <>
      <H1>
        <EditableTitle
          addTitle={modifyTitle}
          modifyTitle={modifyTitle}
          currentTitle={props.class.displayName}
          defaultTitle={DefaultNames.Class}
          isMainTitle={true}
        />
      </H1>
      <TabsContainer>
        <TabButtons tabs={tabs} displayed={tab} setSelected={setSelected} />
        {renderTab()}
      </TabsContainer>
    </>
  );
};

export default ClassTabs;
